import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, PackageCheck, Truck, XCircle, BadgeCheck, Barcode, CheckCircle2, Home } from "lucide-react";
import { toast } from "sonner";
import { formatCLP } from "@/lib/tienda";

const EST = {
  pendiente_pago: { l: "Pendiente de pago", b: "bg-amber-50 text-amber-700" },
  pagado: { l: "POR DESPACHAR", b: "bg-red-50 text-red-700" },
  despachado: { l: "Despachado", b: "bg-blue-50 text-blue-700" },
  entregado: { l: "Entregado", b: "bg-emerald-50 text-emerald-700" },
  cancelado: { l: "Cancelado", b: "bg-gray-100 text-gray-500" },
};

export default function TiendaPedidos() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [estado, setEstado] = useState("pagado");
  const [conteos, setConteos] = useState({});
  const [productos, setProductos] = useState([]);
  // despacho con verificación física: {pedido, verificados:{producto_id:{metodo,codigo}}, scan, seguimiento}
  const [desp, setDesp] = useState(null);

  const load = () => {
    api.get(`/api/tienda/admin/pedidos?estado=${estado}`).then((r) => setPedidos(r.data)).catch(() => {});
    api.get("/api/tienda/admin/pedidos/badge").then((r) => setConteos(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [estado]);
  useEffect(() => {
    api.get("/api/tienda/admin/productos").then((r) => setProductos(r.data)).catch(() => {});
  }, [api]);

  const accion = async (p, path, extra = {}) => {
    try {
      const r = await api.post(`/api/tienda/admin/pedidos/${p.id}/${path}`, extra);
      toast.success(r.data.message); load();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); return false; }
  };

  // ---- verificación de despacho ----
  const abrirDespacho = (p) => setDesp({ pedido: p, verificados: {}, scan: "", seguimiento: "" });

  const buscarPorCodigo = (codigo) => {
    const c = codigo.trim();
    if (!c) return null;
    const cu = c.toUpperCase();
    return productos.find((pr) =>
      (pr.codigo_barras || "") === c || (pr.sku || "") === cu || (pr.codigo || "") === cu);
  };

  const pistolear = () => {
    if (!desp) return;
    const prod = buscarPorCodigo(desp.scan);
    if (!prod) { toast.error("Código no encontrado en el inventario"); return; }
    const item = desp.pedido.items.find((i) => i.producto_id === prod.id);
    if (!item) { toast.error(`"${prod.nombre}" no es parte de este pedido`); return; }
    if (desp.verificados[prod.id]) { toast.info("Ese producto ya está verificado"); setDesp({ ...desp, scan: "" }); return; }
    setDesp({
      ...desp, scan: "",
      verificados: { ...desp.verificados, [prod.id]: { metodo: "pistoleo", codigo: desp.scan.trim() } },
    });
    toast.success(`✓ ${prod.nombre} verificado`);
  };

  const seleccionarInventario = (item, prodId) => {
    if (prodId !== item.producto_id) {
      toast.error("El producto seleccionado no coincide con el del pedido");
      return;
    }
    setDesp((d) => ({
      ...d,
      verificados: { ...d.verificados, [item.producto_id]: { metodo: "inventario", codigo: "" } },
    }));
    toast.success(`✓ ${item.nombre} verificado`);
  };

  const confirmarDespacho = async () => {
    const items = desp.pedido.items || [];
    const faltan = items.filter((i) => !desp.verificados[i.producto_id]);
    if (faltan.length) { toast.error(`Falta verificar: ${faltan.map((i) => i.nombre).join(", ")}`); return; }
    const verificacion = items.map((i) => ({ producto_id: i.producto_id, ...desp.verificados[i.producto_id] }));
    const ok = await accion(desp.pedido, "despachar", { seguimiento: desp.seguimiento.trim(), verificacion });
    if (ok) setDesp(null);
  };

  const pills = [
    ["pagado", "Por despachar", conteos.por_despachar],
    ["pendiente_pago", "Pendientes de pago", conteos.pendiente_pago],
    ["despachado", "Despachados", conteos.despachado],
    ["entregado", "Entregados", conteos.entregado],
    ["todos", "Todos", null],
  ];

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Truck className="h-5 w-5 text-[#E0653A]" />
            <span className="text-sm font-semibold text-[#384091]">Pedidos</span>
            {(conteos.por_despachar || 0) > 0 && (
              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                {conteos.por_despachar}
              </span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-5 space-y-4">
        <div className="flex gap-2 flex-wrap">
          {pills.map(([k, l, n]) => (
            <button key={k} onClick={() => setEstado(k)}
              className={`relative rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                estado === k ? "bg-[#384091] text-white border-[#384091]" : "bg-white text-gray-600 border-gray-200"}`}>
              {l}
              {(n || 0) > 0 && (
                <span className={`absolute -top-1.5 -right-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white ${k === "pagado" ? "bg-red-500" : "bg-gray-400"}`}>
                  {n}
                </span>
              )}
            </button>
          ))}
        </div>

        {pedidos.length === 0 ? (
          <p className="text-center text-gray-400 py-14 text-sm">Sin pedidos en este estado</p>
        ) : pedidos.map((p) => (
          <Card key={p.id} className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-800">
                    {p.folio} <Badge className={`${(EST[p.estado] || {}).b} border-0 ml-1`}>{(EST[p.estado] || {}).l || p.estado}</Badge>
                    {p.factura?.requiere && <Badge className="bg-blue-50 text-blue-700 border-0 ml-1">FACTURA</Badge>}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {p.comprador?.nombre} · {p.comprador?.email} · {p.comprador?.telefono || "sin fono"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {p.envio?.retiro_tienda ? "Retiro en tienda"
                      : `Despacho: ${p.envio?.direccion || ""}, ${p.envio?.comuna || ""} (${p.envio?.region || ""})`}
                  </p>
                  {p.factura?.requiere && (
                    <p className="text-xs text-blue-600">Facturar a: {p.factura.razon_social} · RUT {p.factura.rut}{p.factura.giro ? ` · ${p.factura.giro}` : ""}</p>
                  )}
                  <ul className="mt-2 text-xs text-gray-600">
                    {(p.items || []).map((i, idx) => (
                      <li key={idx}>• {i.nombre} ×{i.cantidad} — {formatCLP(i.subtotal)}</li>
                    ))}
                    {p.envio?.costo > 0 && <li>• Despacho — {formatCLP(p.envio.costo)}</li>}
                  </ul>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-[#384091]">{formatCLP(p.total)}</p>
                  <p className="text-[11px] text-gray-400">{(p.created_at || "").slice(0, 10)}</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {p.estado === "pagado" && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => abrirDespacho(p)}>
                    <PackageCheck className="h-4 w-4 mr-1" /> Despachar (verificar producto)
                  </Button>
                )}
                {p.estado === "despachado" && (
                  <Button size="sm" className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white"
                    onClick={() => window.confirm("¿Confirmas que el pedido fue ENTREGADO al cliente?") && accion(p, "entregar")}>
                    <Home className="h-4 w-4 mr-1" /> Marcar entregado
                  </Button>
                )}
                {p.estado === "pendiente_pago" && (
                  <Button size="sm" variant="outline" className="border-emerald-400 text-emerald-700"
                    onClick={() => window.confirm("¿Confirmas que el pago se recibió (ej. transferencia)?") && accion(p, "marcar-pagado")}>
                    <BadgeCheck className="h-4 w-4 mr-1" /> Marcar pagado
                  </Button>
                )}
                {(p.estado === "pendiente_pago" || p.estado === "pagado") && (
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600"
                    onClick={() => window.confirm("¿Cancelar este pedido? (repone stock si estaba pagado)") && accion(p, "cancelar")}>
                    <XCircle className="h-4 w-4 mr-1" /> Cancelar
                  </Button>
                )}
                {p.seguimiento && <span className="text-xs text-gray-400 self-center">Seguimiento: {p.seguimiento}</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </main>

      {/* Despacho con verificación física (pistoleo o selección del inventario) */}
      <Dialog open={!!desp} onOpenChange={(v) => !v && setDesp(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Despachar {desp?.pedido?.folio}</DialogTitle>
          </DialogHeader>
          {desp && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 -mt-1">
                Verifica físicamente cada producto: <b>pistolea su código de barras/QR</b> o
                selecciónalo del inventario.
              </p>
              <div className="relative">
                <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input autoFocus value={desp.scan}
                  onChange={(e) => setDesp({ ...desp, scan: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); pistolear(); } }}
                  placeholder="Pistolea aquí el código del producto…"
                  className="pl-9 h-11 bg-white no-uppercase" />
              </div>
              <div className="space-y-2">
                {(desp.pedido.items || []).map((i) => {
                  const ok = !!desp.verificados[i.producto_id];
                  return (
                    <div key={i.producto_id}
                      className={`rounded-xl border px-3 py-2.5 ${ok ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-gray-50"}`}>
                      <div className="flex items-center gap-2">
                        {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          : <span className="h-4 w-4 rounded-full border-2 border-gray-300 flex-shrink-0" />}
                        <span className="flex-1 text-sm text-gray-800 truncate">{i.nombre} ×{i.cantidad}</span>
                        {ok && <span className="text-[11px] text-emerald-600 font-semibold">
                          {desp.verificados[i.producto_id].metodo === "pistoleo" ? "PISTOLEADO" : "INVENTARIO"}</span>}
                      </div>
                      {!ok && (
                        <div className="mt-2">
                          <Select onValueChange={(v) => seleccionarInventario(i, v)}>
                            <SelectTrigger className="h-9 bg-white text-xs">
                              <SelectValue placeholder="…o selecciónalo del inventario" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              {productos.map((pr) => (
                                <SelectItem key={pr.id} value={pr.id}>
                                  {pr.nombre}{pr.sku ? ` · ${pr.sku}` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div>
                <Label className="text-xs">N° de seguimiento (opcional)</Label>
                <Input value={desp.seguimiento}
                  onChange={(e) => setDesp({ ...desp, seguimiento: e.target.value })}
                  placeholder="Ej: guía de despacho o tracking del courier"
                  className="mt-1 h-10 bg-white no-uppercase" />
              </div>
              <Button className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                disabled={(desp.pedido.items || []).some((i) => !desp.verificados[i.producto_id])}
                onClick={confirmarDespacho}>
                <PackageCheck className="h-4 w-4 mr-1" /> Confirmar despacho
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
